/* ────────────────────────────────────────────────────────────────────────────
 * MOCK CLIP RENDERER
 * ────────────────────────────────────────────────────────────────────────────
 * Belongs to the MOCK provider only. Delete this file once Maestro is wired in.
 *
 * Renders a procedural animation to an offscreen canvas and records it with
 * MediaRecorder, so a "generation" produces a genuine, playable, downloadable
 * video file rather than a static placeholder. The animation is seeded from the
 * prompt, so the same prompt yields the same clip — like a real seed would.
 *
 * If the browser has no MediaRecorder/captureStream, we fall back to a single
 * rendered still frame (PNG) and flag it via `isStillFallback`.
 * ──────────────────────────────────────────────────────────────────────────── */

import type { GenerationRequest, GenerationResult } from './types';

const RESOLUTION_DIMS: Record<string, { w: number; h: number }> = {
  '480p': { w: 854, h: 480 },
  '720p': { w: 1280, h: 720 },
  '1080p': { w: 1920, h: 1080 },
};

/** Small deterministic PRNG (mulberry32). */
function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(input: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

interface Palette {
  bg: [number, number, number];
  a: [number, number, number];
  b: [number, number, number];
  c: [number, number, number];
}

/**
 * Picks a palette influenced by words in the prompt, so the output visibly
 * "responds" to what the user typed. Purely cosmetic — a real engine does the
 * real thing here.
 */
function paletteFor(prompt: string, rng: () => number): Palette {
  const p = prompt.toLowerCase();
  const has = (...words: string[]) => words.some((w) => p.includes(w));

  if (has('ocean', 'sea', 'water', 'víz', 'tenger', 'wave', 'hullám', 'underwater'))
    return { bg: [4, 14, 24], a: [56, 220, 235], b: [30, 110, 220], c: [140, 250, 220] };
  if (has('fire', 'lava', 'tűz', 'ember', 'volcano', 'burn', 'sunset', 'naplemente'))
    return { bg: [22, 7, 4], a: [255, 130, 40], b: [230, 45, 60], c: [255, 220, 120] };
  if (has('forest', 'erdő', 'jungle', 'moss', 'green', 'zöld', 'leaf', 'növény'))
    return { bg: [6, 16, 10], a: [90, 230, 130], b: [30, 140, 90], c: [220, 250, 150] };
  if (has('neon', 'cyberpunk', 'city', 'város', 'tokyo', 'street', 'night', 'éjszaka'))
    return { bg: [8, 5, 18], a: [255, 60, 160], b: [70, 120, 255], c: [90, 250, 230] };
  if (has('desert', 'sand', 'sivatag', 'dune', 'homok', 'canyon'))
    return { bg: [24, 15, 8], a: [240, 180, 90], b: [190, 100, 50], c: [255, 235, 190] };
  if (has('snow', 'ice', 'hó', 'jég', 'winter', 'tél', 'frost', 'arctic'))
    return { bg: [8, 14, 22], a: [180, 225, 255], b: [90, 150, 220], c: [240, 250, 255] };
  if (has('space', 'űr', 'galaxy', 'star', 'csillag', 'nebula', 'cosmos', 'bolygó'))
    return { bg: [5, 4, 14], a: [150, 110, 255], b: [60, 190, 240], c: [255, 200, 240] };

  // Default: the app's own signature cyan/coral, jittered by seed.
  const jitter = () => Math.floor(rng() * 40) - 20;
  return {
    bg: [7, 9, 13],
    a: [61 + jitter(), 224 + jitter(), 198 + jitter()],
    b: [45 + jitter(), 120 + jitter(), 220 + jitter()],
    c: [255, 120 + jitter(), 130 + jitter()],
  };
}

const rgb = (c: [number, number, number], alpha = 1) =>
  `rgba(${c[0] | 0}, ${c[1] | 0}, ${c[2] | 0}, ${alpha})`;

interface DrawState {
  palette: Palette;
  rng: () => number;
  blobs: { x: number; y: number; r: number; sx: number; sy: number; hue: 0 | 1 | 2 }[];
  motes: { x: number; y: number; z: number; s: number }[];
  image: HTMLImageElement | null;
  label: string;
}

function buildState(request: GenerationRequest, image: HTMLImageElement | null): DrawState {
  const rng = makeRng(request.seed);
  const palette = paletteFor(request.prompt, rng);

  const blobs = Array.from({ length: 7 }, () => ({
    x: rng(),
    y: rng(),
    r: 0.18 + rng() * 0.38,
    sx: (rng() - 0.5) * 0.16,
    sy: (rng() - 0.5) * 0.16,
    hue: Math.floor(rng() * 3) as 0 | 1 | 2,
  }));

  const motes = Array.from({ length: 90 }, () => ({
    x: rng(),
    y: rng(),
    z: 0.2 + rng() * 0.8,
    s: 0.4 + rng() * 1.8,
  }));

  return { palette, rng, blobs, motes, image, label: request.prompt.trim() };
}

/**
 * Draws one frame. `t` is 0..1 across the clip.
 * The look: a slow drifting light field, a horizon sweep, drifting motes, and
 * a settling "denoise" grain that clears as the clip progresses.
 */
function drawFrame(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  t: number,
  st: DrawState,
): void {
  const { palette: pal } = st;
  const tau = Math.PI * 2;

  // Base wash.
  const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
  bgGrad.addColorStop(0, rgb(pal.bg));
  bgGrad.addColorStop(1, `rgb(${pal.bg[0] + 10}, ${pal.bg[1] + 12}, ${pal.bg[2] + 18})`);
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, w, h);

  // If an input still was supplied (image-to-video), ghost it in and let the
  // light field animate over it, so i2v visibly differs from t2v.
  if (st.image) {
    const img = st.image;
    const scale = Math.max(w / img.width, h / img.height) * (1 + t * 0.08); // slow push-in
    const dw = img.width * scale;
    const dh = img.height * scale;
    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    ctx.restore();
  }

  // Drifting colour blobs.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const b of st.blobs) {
    const bx = (b.x + Math.sin(t * tau + b.x * 9) * b.sx + 1) % 1;
    const by = (b.y + Math.cos(t * tau + b.y * 7) * b.sy + 1) % 1;
    const cx = bx * w;
    const cy = by * h;
    const rad = b.r * Math.min(w, h) * (0.8 + Math.sin(t * tau * 2 + b.r * 10) * 0.2);
    const color = b.hue === 0 ? pal.a : b.hue === 1 ? pal.b : pal.c;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, rad);
    g.addColorStop(0, rgb(color, st.image ? 0.3 : 0.5));
    g.addColorStop(0.45, rgb(color, st.image ? 0.1 : 0.18));
    g.addColorStop(1, rgb(color, 0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, tau);
    ctx.fill();
  }
  ctx.restore();

  // Horizon band + sweeping play-head glow.
  const horizonY = h * (0.62 + Math.sin(t * tau) * 0.015);
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const hg = ctx.createLinearGradient(0, horizonY - h * 0.16, 0, horizonY + h * 0.16);
  hg.addColorStop(0, rgb(pal.a, 0));
  hg.addColorStop(0.5, rgb(pal.a, 0.22));
  hg.addColorStop(1, rgb(pal.a, 0));
  ctx.fillStyle = hg;
  ctx.fillRect(0, horizonY - h * 0.16, w, h * 0.32);

  const sweepX = ((t * 1.35) % 1) * w;
  const sg = ctx.createLinearGradient(sweepX - w * 0.14, 0, sweepX + w * 0.14, 0);
  sg.addColorStop(0, rgb(pal.c, 0));
  sg.addColorStop(0.5, rgb(pal.c, 0.16));
  sg.addColorStop(1, rgb(pal.c, 0));
  ctx.fillStyle = sg;
  ctx.fillRect(sweepX - w * 0.14, 0, w * 0.28, h);
  ctx.restore();

  // Perspective grid below the horizon — the "generated space" motif.
  ctx.save();
  ctx.strokeStyle = rgb(pal.a, 0.16);
  ctx.lineWidth = Math.max(1, w / 1600);
  for (let i = 1; i <= 16; i++) {
    const p = i / 16;
    const y = horizonY + Math.pow(p, 2.1) * (h - horizonY) * 1.05;
    if (y > h) break;
    ctx.globalAlpha = 1 - p * 0.75;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  const vanishX = w * 0.5 + Math.sin(t * tau) * w * 0.06;
  for (let i = -14; i <= 14; i++) {
    ctx.globalAlpha = 0.5 - Math.abs(i) / 40;
    ctx.beginPath();
    ctx.moveTo(vanishX, horizonY);
    ctx.lineTo(vanishX + i * w * 0.16, h);
    ctx.stroke();
  }
  ctx.restore();

  // Floating motes.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const m of st.motes) {
    const mx = ((m.x + t * 0.12 * m.z) % 1) * w;
    const my = ((m.y - t * 0.07 * m.z + 1) % 1) * h;
    const r = m.s * (w / 1280) * 1.6;
    ctx.fillStyle = rgb(pal.c, 0.06 + m.z * 0.22);
    ctx.beginPath();
    ctx.arc(mx, my, r, 0, tau);
    ctx.fill();
  }
  ctx.restore();

  // "Denoise" grain that clears over the clip — reads as diffusion settling.
  const grainStrength = Math.max(0, 0.5 - t * 0.55);
  if (grainStrength > 0.005) {
    ctx.save();
    ctx.globalAlpha = grainStrength * 0.5;
    const cell = Math.max(3, Math.round(w / 220));
    for (let gy = 0; gy < h; gy += cell) {
      for (let gx = 0; gx < w; gx += cell) {
        if (st.rng() > 0.55) continue;
        const v = st.rng();
        ctx.fillStyle = `rgba(${(v * 255) | 0}, ${(v * 255) | 0}, ${(v * 255) | 0}, 0.5)`;
        ctx.fillRect(gx, gy, cell, cell);
      }
    }
    ctx.restore();
  }

  // Vignette.
  const vg = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.25, w / 2, h / 2, Math.max(w, h) * 0.75);
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(0,0,0,0.62)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, h);

  // Burned-in slate so an exported file is obviously a mock artefact.
  const pad = Math.round(w * 0.028);
  const fs = Math.max(11, Math.round(w / 74));
  ctx.save();
  ctx.font = `600 ${fs}px ui-monospace, "Cascadia Mono", "Consolas", monospace`;
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.fillText('MOCK RENDER · NO ENGINE ATTACHED', pad, pad);

  ctx.textBaseline = 'bottom';
  ctx.fillStyle = 'rgba(255,255,255,0.72)';
  const label = st.label.length > 58 ? `${st.label.slice(0, 57)}…` : st.label;
  ctx.fillText(label || 'untitled', pad, h - pad);

  const tc = `${String(Math.floor(t * 3)).padStart(2, '0')}:${String(Math.floor(t * 3 * 24) % 24).padStart(2, '0')}`;
  ctx.textAlign = 'right';
  ctx.fillStyle = rgb(pal.a, 0.85);
  ctx.fillText(tc, w - pad, h - pad);
  ctx.restore();
}

/** Picks a container/codec the browser can actually record. */
function pickMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
    'video/mp4',
  ];
  for (const c of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

export interface RenderClipOptions {
  request: GenerationRequest;
  /** Wall-clock length of the produced clip, in seconds. */
  durationSeconds: number;
  signal: AbortSignal;
  /** 0..1 progress of the recording itself. */
  onTick?: (t: number) => void;
}

const abortError = () =>
  new DOMException('Generation cancelled.', 'AbortError');

/**
 * Renders + records the clip in real time. Resolves with a playable object URL.
 */
export async function renderClip(options: RenderClipOptions): Promise<GenerationResult> {
  const { request, durationSeconds, signal } = options;
  if (signal.aborted) throw abortError();

  const dims = RESOLUTION_DIMS[request.settings.resolution] ?? RESOLUTION_DIMS['720p'];
  // Cap the recorded canvas so 1080p does not choke slower machines; the
  // reported result still carries the requested dimensions.
  const scale = dims.w > 1280 ? 1280 / dims.w : 1;
  const w = Math.round(dims.w * scale);
  const h = Math.round(dims.h * scale);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('2D canvas context is unavailable.');

  const image = request.imageDataUrl ? await loadImage(request.imageDataUrl) : null;
  const state = buildState(request, image);

  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const slug =
    request.prompt
      .toLowerCase()
      // Strip Hungarian (and other) diacritics so "ő" becomes "o", not a dash.
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40)
      .replace(/-+$/, '') || 'clip';

  const mimeType = pickMimeType();
  const canCapture = typeof canvas.captureStream === 'function';

  // ── Fallback: no MediaRecorder → deliver a single rendered frame. ──────────
  if (!mimeType || !canCapture) {
    drawFrame(ctx, w, h, 0.85, state);
    const still = canvas.toDataURL('image/png');
    return {
      videoUrl: still,
      posterUrl: still,
      mimeType: 'image/png',
      filename: `${slug}-${stamp}.png`,
      durationSeconds,
      width: dims.w,
      height: dims.h,
      isStillFallback: true,
    };
  }

  const stream = canvas.captureStream(30);
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 6_000_000,
  });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  const stopped = new Promise<void>((resolve) => {
    recorder.onstop = () => resolve();
  });

  let timerId = 0;
  let poster = '';

  const finished = new Promise<void>((resolve, reject) => {
    const start = performance.now();
    const durationMs = durationSeconds * 1000;

    const onAbort = () => {
      window.clearTimeout(timerId);
      try {
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* ignore */
      }
      reject(abortError());
    };
    signal.addEventListener('abort', onAbort, { once: true });

    // NOTE: a timer loop, deliberately not requestAnimationFrame. rAF stops
    // firing in a hidden or non-compositing tab, which would leave the job
    // hanging forever; a timer keeps ticking (throttled, so the clip gets
    // fewer frames) and the run always finishes on the wall clock.
    const loop = () => {
      if (signal.aborted) return;
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      drawFrame(ctx, w, h, t, state);
      options.onTick?.(t);

      // Grab the poster a bit past the middle, once the grain has cleared.
      if (!poster && t >= 0.72) poster = canvas.toDataURL('image/jpeg', 0.82);

      if (t >= 1) {
        signal.removeEventListener('abort', onAbort);
        if (!poster) poster = canvas.toDataURL('image/jpeg', 0.82);
        try {
          if (recorder.state !== 'inactive') recorder.stop();
        } catch {
          /* ignore */
        }
        resolve();
        return;
      }
      timerId = window.setTimeout(loop, 1000 / 30);
    };

    recorder.start(100);
    loop();
  });

  try {
    await finished;
  } catch (err) {
    stream.getTracks().forEach((t) => t.stop());
    throw err;
  }

  await stopped;
  stream.getTracks().forEach((t) => t.stop());

  const container = mimeType.startsWith('video/mp4') ? 'video/mp4' : 'video/webm';
  const ext = container === 'video/mp4' ? 'mp4' : 'webm';
  const blob = new Blob(chunks, { type: container });
  const videoUrl = URL.createObjectURL(blob);

  return {
    videoUrl,
    posterUrl: poster,
    mimeType: container,
    filename: `${slug}-${stamp}.${ext}`,
    durationSeconds,
    width: dims.w,
    height: dims.h,
  };
}
